/**
 * Experience Service
 *
 * API service for fetching experiences from the backend
 *
 * This service layer uses the repository pattern following SOLID principles:
 * - Single Responsibility: Delegates data operations to repository
 * - Dependency Inversion: Depends on repository abstraction
 * - Open/Closed: Can be extended without modifying existing code
 */

import { ExperienceRepositoryFactory } from './api/factories/ExperienceRepositoryFactory';

export interface ExperienceQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: Record<string, string | number>;
}

export interface LocationFilter {
  lat: number;
  lng: number;
  name: string;
  locationType?: string; // Location type (e.g., "State", "District", "City")
}

export interface ExperienceFilters {
  type?: string;
  categories?: string[]; // Changed from category/tags to categories array
  tags?: string[]; // Keep for backward compatibility
  category?: string; // Keep for backward compatibility
  curated?: boolean;
  guide?: string;
  price?: {
    min?: number;
    max?: number;
  };
  travellerType?: string[];
  locations?: LocationFilter[]; // Array of location objects with lat, lng, and name
  lat?: number; // Keep for backward compatibility
  lng?: number; // Keep for backward compatibility
  maxDistance?: number;
  locName?: string; // Keep for backward compatibility
  locType?: string;
  random?: boolean; // Randomize the results
}

export interface ExperienceRequest {
  queryParams?: ExperienceQueryParams;
  filters?: ExperienceFilters;
}

export interface Experience {
  _id: string;
  name: string;
  title?: string; // Make title optional to handle API variations
  shortTitle?: string | null;
  slug: string;
  code: string;
  type: string;
  category: {
    title: string;
    description: string;
    icon?: string;
    imageUrl?: string;
    _id: string;
  };
  guide?: {
    _id: string;
    name: string;
    code: string;
  } | null;
  location?: {
    _id: string;
    name: string;
    state: string;
    city: string;
  } | null;
  imageUrl: string[];
  media?: Array<{
    _id: string;
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    url: string;
    thumbnailUrl?: string;
    thumbnailSet?: {
      mobile?: string;
      tablet?: string;
      desktop?: string;
    };
    format: string; // 'image' | 'video'
    entityType: string;
    entityId: string;
    category: string; // 'banner' | 'gallery' | 'video'
    tags?: string[];
    isActive: boolean;
    isPublic: boolean;
    uploadedBy: string;
    createdAt: string;
    updatedAt: string;
  }>;
  price?: number | null;
  currencySymbol?: string | null;
  pricing?: {
    _id?: string;
    currency?: string;
    currencySymbol?: string;
    pricePerPerson?: number;
    childPrice?: number;
    priceDescription?: string;
    groupPricing?: Array<{
      isDisplayPrice?: boolean;
      minHeadCount?: number;
      maxHeadCount?: number;
      adultPrice?: number;
      childPrice?: number;
      description?: string;
    }>;
    isChildAllowed?: boolean;
    minBooking?: number;
    maxBooking?: number;
  } | null;
  averageRating: number;
  reviewCount: number;
  isActive: boolean;
  curated: boolean;
  createdAt: string;
  description?: string;
  duration?: {
    days?: number;
    nights?: number;
    hours?: number;
  } | null;
  attractions?: Array<{
    _id: string;
    title: string;
    imageUrls?: string[];
    time?: number;
    startTime?: string;
    endTime?: string;
    description?: string;
    place?: string;
  }> | null;
  /** Attraction count from list/detail API – prefer over attractions.length */
  attractionCount?: number;
  highlightsCount?: number;

  // Backward compatibility (computed properties for components)
  id?: string;
  image?: string;
}

export interface ExperienceListResponse {
  experiences: Experience[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  totalPages?: number;
}

/**
 * Fetch experiences from the API
 * Uses repository pattern for better separation of concerns
 */
export async function fetchExperiences(
  request: ExperienceRequest = {},
  accessToken?: string,
  refreshToken?: string
): Promise<ExperienceListResponse> {
  if (process.env.NODE_ENV === 'development') {
    console.log('fetchExperiences:', {
      request,
      hasAuth: !!(accessToken && refreshToken),
    });
  }

  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getExperiences(request, accessToken, refreshToken);
}

/**
 * Fetch featured experiences for landing page (6 items)
 * Uses repository pattern for better separation of concerns
 */
export async function fetchFeaturedExperiences(
  accessToken?: string,
  refreshToken?: string
): Promise<Experience[]> {
  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getFeaturedExperiences(accessToken, refreshToken);
}

/**
 * Fetch signature journeys for landing page (3 items)
 * These are curated packages (curated: true, type: 'package')
 * Uses repository pattern for better separation of concerns
 */
export async function fetchSignatureJourneys(
  accessToken?: string,
  refreshToken?: string
): Promise<Experience[]> {
  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getSignatureJourneys(accessToken, refreshToken);
}

/**
 * Fetch curated packages for landing page (3 items)
 * These are non-curated packages (curated: false, type: 'package')
 * Uses repository pattern for better separation of concerns
 */
export async function fetchCuratedPackages(
  accessToken?: string,
  refreshToken?: string
): Promise<Experience[]> {
  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getCuratedPackages(accessToken, refreshToken);
}

/**
 * Fetch boutique stays for landing page (4 items)
 * These are experiences with type 'stay_experience'
 * Uses repository pattern for better separation of concerns
 */
export async function fetchBoutiqueStays(
  accessToken?: string,
  refreshToken?: string
): Promise<Experience[]> {
  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getBoutiqueStays(accessToken, refreshToken);
}

/**
 * Fetch guided activities for landing page (3 items)
 * These are experiences with type 'activity'
 * Uses repository pattern for better separation of concerns
 */
export async function fetchGuidedActivities(
  accessToken?: string,
  refreshToken?: string
): Promise<Experience[]> {
  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getGuidedActivities(accessToken, refreshToken);
}

/**
 * Fetch riders corner for landing page (2 items)
 * These are experiences with type 'riders_corner'
 * Uses repository pattern for better separation of concerns
 */
export async function fetchRidersCorner(
  accessToken?: string,
  refreshToken?: string
): Promise<Experience[]> {
  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getRidersCorner(accessToken, refreshToken);
}

/**
 * Experience Statistics Interface
 */
export interface ExperienceStats {
  signatureJourneys: number;
  curatedPackages: number;
  boutiqueStays: number;
  guidedActivities: number;
  ridersGuides: number;
  totalExperiences?: number;
}

/**
 * Fetch experience statistics for About Us section
 * Uses repository pattern for better separation of concerns
 */
export async function fetchExperienceStats(
  accessToken?: string,
  refreshToken?: string
): Promise<ExperienceStats> {
  const repository = ExperienceRepositoryFactory.getInstance();
  return repository.getExperienceStats(accessToken, refreshToken);
}

/**
 * Get display image from experience media array
 * Priority: banner image > first image > imageUrl fallback > placeholder
 */
export function getExperienceImage(experience: Experience): string {
  // First, try to get banner image from media
  if (
    experience.media &&
    Array.isArray(experience.media) &&
    experience.media.length > 0
  ) {
    // Look for banner category image first
    const bannerImage = experience.media.find(
      item =>
        item.format === 'image' && item.category === 'banner' && item.isActive
    );
    if (bannerImage?.url) {
      return bannerImage.url;
    }

    // If no banner, get first image from media
    const firstImage = experience.media.find(
      item => item.format === 'image' && item.isActive
    );
    if (firstImage?.url) {
      return firstImage.url;
    }
  }

  // Fallback to imageUrl array
  if (
    experience.imageUrl &&
    Array.isArray(experience.imageUrl) &&
    experience.imageUrl.length > 0
  ) {
    return experience.imageUrl[0];
  }

  // Final fallback to placeholder image
  // Use a default placeholder image to prevent Next.js Image errors
  return 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80';
}
